// Shared print helper for the finance / cashier reports. The hotel prints on
// two very different devices: a thermal 80mm receipt printer (cashier till
// receipts, small reports) and an A4 sheet printer (management reports). The
// report pages build their own body HTML (tables of numbers) and hand it to
// printDocument(), which wraps it in a full document sized for the chosen
// device and fires window.print(). Frontend-only, no dependencies.
//
// Usage:
//   printDocument({ title, bodyHtml, format })   // format: "thermal" | "a4"

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// Shared base — black text on white, system sans-serif, full-width tables,
// right-aligned money via .r, print margin reset.
const BASE_CSS = `
  body{color:#000;background:#fff;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}
  table{width:100%;border-collapse:collapse}
  td,th{padding:2px 6px;text-align:left}
  .r{text-align:right}
  h2{font-size:16px}
  h3{font-size:13px}
  @media print{body{margin:0}}
`;

// Thermal 80mm receipt roll — compact, single column, thin black separators,
// no background fills, money right-aligned.
const THERMAL_CSS = `
  @page{ size:80mm auto; margin:3mm }
  body{ width:72mm; font-size:11px; line-height:1.35 }
  h2{font-size:13px;margin:0 0 4px}
  h3{font-size:11px;margin:8px 0 2px;border-bottom:1px solid #000}
  td,th{padding:1px 2px}
`;

// A4 management report — centred block, roomier, teal section headers.
const A4_CSS = `
  @page{ size:A4; margin:14mm }
  body{ max-width:720px; margin:0 auto; font-size:12.5px }
  h2{margin:0 0 4px}
  h3{color:#0F6E56;border-bottom:1px solid #ccc;font-size:12px;text-transform:uppercase;margin:14px 0 4px}
  td,th{border-bottom:1px solid #eee}
`;

export function printDocument({ title, bodyHtml, format = "a4" } = {}) {
  const w = window.open("", "_blank");
  if (!w) return;
  const fmtCss = format === "thermal" ? THERMAL_CSS : A4_CSS;
  w.document.write(
    `<html><head><title>${esc(title)}</title>`
    + `<style>${BASE_CSS}${fmtCss}</style></head>`
    + `<body>${bodyHtml || ""}`
    + `<scr` + `ipt>window.onload=function(){window.print()}<\/scr` + `ipt></body></html>`
  );
  w.document.close();
}

// Convenience wrapper for the report pages: build a titled A4 sheet from a list
// of sections, each a { heading, headers:[...], rows:[[...],...] } table (a row
// may set {bold:true} on its array via row.bold for a totals line). Keeps the
// per-page print code to just assembling `sections` from the loaded data.
export function printTableReport({ title, subtitle, sections = [], format = "a4" } = {}) {
  const th = (h) => `<th class="r">${esc(h)}</th>`;
  const tableHtml = (s) => {
    const heads = (s.headers || []).map((h, i) => (i === 0 ? `<th>${esc(h)}</th>` : th(h))).join("");
    const body = (s.rows || []).map((r) => {
      const cells = r.map((c, i) => (i === 0 ? `<td>${esc(c)}</td>` : `<td class="r">${esc(c)}</td>`)).join("");
      return `<tr${r.bold ? ' style="font-weight:700"' : ""}>${cells}</tr>`;
    }).join("");
    return (s.heading ? `<h3>${esc(s.heading)}</h3>` : "")
      + `<table><thead><tr>${heads}</tr></thead><tbody>${body}</tbody></table>`;
  };
  const bodyHtml = `<h2>${esc(title)}</h2>`
    + (subtitle ? `<div style="color:#555;margin-bottom:8px">${esc(subtitle)}</div>` : "")
    + sections.map(tableHtml).join("");
  printDocument({ title, bodyHtml, format });
}
